/* eslint-disable react/no-unused-prop-types */
// Tons of props used by styles.ts unfortunately.
import React, {
  FC,
  useRef,
  useEffect,
  useState,
  createElement,
  ReactNode,
  useContext,
} from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 } from 'uuid';
import { useRouter } from 'Utils/router';
import Animations from '../../animations';
import styleSheet from './styles';
import Portal from '../Portal/Portal';

interface Props {
  delay?: number;
  width?: string;
  wordBreak?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  text: string | ReactNode;
  offsetTop?: number;
  offsetLeft?: number;
  classes?: any;
  children: any;
  overrideEvents?: {
    // Stringified HTML event names
    showToolTip: string[];
    hideToolTip: string[];
  };
  display?: string;
  backgroundColor?: string;
  arrowColor?: string;
  disableArrow?: boolean;
  ariaLabel?: string;
  parentText?: string; // This is used for Combined Analyrics, when text contains a ReactNode.  Combined Analytics needs a string
}

const Tooltip: FC<Props> = (props: Props) => {
  const [id] = useState(v4());
  const [shown, setShown] = useState(false);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [time, setTime] = useState(new Date().getTime()); // Default, will never be used

  const {
    delay,
    position,
    text,
    offsetTop,
    offsetLeft,
    classes,
    children,
    overrideEvents,
    disableArrow,
    ariaLabel,
    parentText,
  } = props;

  const toolTip = useRef(null);
  const childRef = useRef(null);

  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();

  const showToolTip = () => {
    // Combined Analytics event tracking
    const date = new Date();
    // Combined Analytics event tracking
    track('tooltip_shown', {
      screen: View(location.pathname),
      tooltip_text: typeof text === 'string' ? text : parentText,
      tooltip_shown_timestamp: `${date}`,
    });
    setTime(date.getTime());

    const element = document.getElementById(id);

    if (element) {
      setTop(element.getBoundingClientRect().top + window.scrollY);
      setLeft(element.getBoundingClientRect().left + window.scrollX);
    }
    setShown(true);
  };

  const hideToolTip = () => {
    if (shown) {
      // Combined Analytics event tracking
      track('tooltip_dismissed', {
        screen: View(location.pathname),
        tooltip_text: typeof text === 'string' ? text : parentText,
        tooltip_dismissed_timestamp: `${new Date()}`,
        tooltip_timespent: `${Math.abs(
          (new Date().getTime() - time) / 1000
        )} sec`, // Convert the milliseconds to seconds
      });
    }

    setShown(false);
  };

  useEffect(() => {
    if (toolTip.current) {
      if (shown) {
        Animations.toolTip.in(toolTip, position, 0, 0, 0, delay || 0);
      } else {
        Animations.toolTip.out(toolTip, position, 0, 0, 0);
      }
    }
  }, [shown]);

  const showToolTipEvents = (overrideEvents &&
    overrideEvents.showToolTip.reduce((events, event) => {
      return {
        ...events,
        [event]: showToolTip,
      };
    }, {})) || { onMouseEnter: showToolTip };

  const hideToolTipEvents = (overrideEvents &&
    overrideEvents.hideToolTip.reduce((events, event) => {
      return {
        ...events,
        [event]: hideToolTip,
      };
    }, {})) || { onMouseLeave: hideToolTip };

  const child = createElement(
    'div',
    {
      id,
      ref: childRef,
      ...showToolTipEvents,
      ...hideToolTipEvents,
    },
    children
  );

  useEffect(() => {
    window.addEventListener('scroll', hideToolTip, true);
  }, []);

  return (
    <div className={classes.container} aria-label={ariaLabel}>
      {child}
      {shown && (
        <Portal>
          <div
            ref={toolTip}
            className={classNames(
              classes.tooltip,
              classes[`tooltip_${position}`]
            )}
            style={{
              top: `${top + (offsetTop || 0)}px`,
              left: `${left + (offsetLeft || 0)}px`,
              visibility: 'hidden',
            }}
          >
            <span style={{ textTransform: 'none' }} aria-label={ariaLabel}>
              {text}
            </span>
            {!disableArrow && (
              <div
                className={classNames(
                  classes.arrow,
                  classes[`arrow_${position}`]
                )}
              />
            )}
          </div>
        </Portal>
      )}
    </div>
  );
};

Tooltip.defaultProps = {
  delay: 0,
  width: undefined,
  wordBreak: undefined,
  position: 'top',
  offsetTop: 0,
  offsetLeft: 0,
  classes: {},
  overrideEvents: undefined,
  display: undefined,
  backgroundColor: undefined,
  arrowColor: undefined,
  disableArrow: false,
  ariaLabel: '',
  parentText: '',
};

export default injectSheet(styleSheet)(Tooltip);
